// If the map bounds depends on the list of projects with the current filter,
// then the user sometimes zooms to a place of interest, switches filters
// (often from their own user to 'all contributors'), and the map zooms away.
// This is experienced as a bug (#3633).

// Solution: compute the bounds of the map based on all projects in
// the tenant, so that it stays the same regardless of filters.

import { useMemo, useRef } from "react";
import { useMap } from "react-map-gl/maplibre";

import { useMapListener } from "@nens/react/hooks";
import { Extent, combineExtents } from "@nens/shared-utils";

import { MapProject } from "../types/map-project";

export const useProjectListMapBounds = ({
  projectsOnMap,
  mapId,
}: {
  projectsOnMap: MapProject[];
  mapId: string;
}): Extent | null => {
  const { [mapId]: map } = useMap();
  const boundsWhenInteractHappenedRef = useRef<Extent | null>(null);

  const projectBounds = useMemo(() => {
    // Turn the points into point-size extents
    const extents = projectsOnMap.map(({ location }) => {
      const { coordinates } = location;
      return [coordinates[0], coordinates[1], coordinates[0], coordinates[1]] as Extent;
    });

    if (!extents.length) {
      return null;
    }

    // Combine them into one
    return combineExtents(extents);
  }, [projectsOnMap]);

  const firstValidBounds = useRef<Extent | null>(null);
  if (!firstValidBounds.current && projectBounds) {
    firstValidBounds.current = projectBounds;
  }

  const onInteract = () => {
    if (boundsWhenInteractHappenedRef.current) return;
    boundsWhenInteractHappenedRef.current = projectBounds;
  };

  useMapListener("dragstart", onInteract, map);
  useMapListener("wheel", onInteract, map);

  return boundsWhenInteractHappenedRef.current ?? projectBounds ?? firstValidBounds.current;
};
